import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
  BarChart3,
  Trophy,
  Zap,
  TrendingUp,
  Video,
  Loader2,
  Lock,
  Crown,
  Target,
  Award,
  Star,
  ChevronRight,
  Sparkles,
  Play,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Advanced Stats Modal
 * 
 * Shows SportsVisio-powered stats for athletes.
 * - Elite tier users: Full access to stats
 * - Non-Elite users: See upgrade prompt
 * - Unlinked athletes: Show "Stats not available yet"
 */

// Trophy badge definitions with icons and colors
const TROPHY_CONFIG = {
  potg: { label: 'Player of the Game', icon: Trophy, color: 'text-amber-400 bg-amber-500/20 border-amber-500/30' },
  sniper: { label: 'Sniper', icon: Target, color: 'text-red-400 bg-red-500/20 border-red-500/30' },
  thief: { label: 'Thief', icon: Zap, color: 'text-purple-400 bg-purple-500/20 border-purple-500/30' },
  smoothOperator: { label: 'Smooth Operator', icon: Star, color: 'text-cyan-400 bg-cyan-500/20 border-cyan-500/30' },
  dimer: { label: 'Dimer', icon: Sparkles, color: 'text-green-400 bg-green-500/20 border-green-500/30' },
  doubleBadge: { label: 'Double-Double', icon: Award, color: 'text-orange-400 bg-orange-500/20 border-orange-500/30' },
};

const AdvancedStatsModal = ({ open, onOpenChange, athlete }) => {
  const { isAuthenticated, hasRequiredTier, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [sportsvisioStatus, setSportsvisioStatus] = useState(null);
  const [stats, setStats] = useState(null);

  // Check if user has Elite access
  const hasEliteAccess = isAuthenticated() && hasRequiredTier('elite');
  
  // Check if this is the athlete's own profile (they can see their own stats)
  const isOwnProfile = user?.id === athlete?.claimed_by;

  // Either Elite member OR viewing own claimed profile
  const canViewStats = hasEliteAccess || isOwnProfile;

  const fetchSportsVisioStatus = useCallback(async () => {
    if (!athlete?.id) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/athletes/${athlete.id}/sportsvisio/status`);
      if (res.ok) {
        const data = await res.json();
        setSportsvisioStatus(data);
        
        // If linked and has synced stats, fetch the cached stats
        if (data.linked && data.has_synced_stats) {
          const cacheRes = await fetch(`${API_URL}/api/sportsvisio/cache/stats/${athlete.id}`);
          if (cacheRes.ok) {
            const cacheData = await cacheRes.json();
            if (cacheData.cached) {
              setStats(cacheData.data);
            }
          }
        }
      }
    } catch (err) {
      console.error('Failed to fetch SportsVisio status:', err);
    }
    setLoading(false);
  }, [athlete?.id]);

  useEffect(() => {
    if (open && athlete?.id) {
      fetchSportsVisioStatus();
    }
  }, [open, athlete?.id, fetchSportsVisioStatus]);

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setStats(null);
      setSportsvisioStatus(null);
    }
  }, [open]);

  if (!athlete) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0A0A0A] border-white/10 max-w-lg sm:max-w-xl overflow-hidden p-0">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#ff0000]/20 via-[#141414] to-[#ff0000]/10 border-b border-white/10 p-5">
          <DialogHeader>
            <div className="flex items-center gap-4">
              <img
                src={athlete.avatar || `https://api.dicebear.com/7.x/avataaars/svg?seed=${athlete.id}`}
                alt={athlete.name}
                className="w-14 h-14 rounded-full border-2 border-[#ff0000]/50"
              />
              <div>
                <DialogTitle 
                  className="text-xl font-black text-white uppercase tracking-tight"
                  style={{ fontFamily: 'Oswald, sans-serif' }}
                >
                  {athlete.name}
                </DialogTitle>
                <p className="text-sm text-zinc-400">{athlete.position} • {athlete.team}</p>
              </div>
            </div>
          </DialogHeader>
        </div>

        {/* Content */}
        <div className="p-5">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 text-[#ff0000] animate-spin" />
            </div>
          ) : !canViewStats ? (
            // Non-Elite User - Show Upgrade Prompt
            <EliteUpgradePrompt athleteName={athlete.name} />
          ) : !sportsvisioStatus?.linked ? (
            // Stats not linked yet
            <StatsNotAvailable athleteName={athlete.name} />
          ) : !sportsvisioStatus?.has_synced_stats ? (
            // Linked but no stats synced yet
            <StatsPending athleteName={athlete.name} />
          ) : (
            // Full Stats View
            <FullStatsView athlete={athlete} stats={stats} />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Elite Upgrade Prompt - Shown to non-Elite users
 */
const EliteUpgradePrompt = ({ athleteName }) => (
  <div className="text-center py-6">
    <div className="w-16 h-16 rounded-full bg-amber-500/20 flex items-center justify-center mx-auto mb-4">
      <Crown className="h-8 w-8 text-amber-400" />
    </div>
    <h3 
      className="text-xl font-black text-white uppercase tracking-tight mb-2"
      style={{ fontFamily: 'Oswald, sans-serif' }}
    >
      Elite Access Required
    </h3>
    <p className="text-zinc-400 text-sm mb-6 max-w-sm mx-auto">
      Upgrade to <span className="text-amber-400 font-semibold">Rooam Elite</span> to unlock advanced stats, 
      AI performance analysis, and highlight reels for {athleteName} and all athletes.
    </p>
    
    {/* Feature Preview */}
    <div className="grid grid-cols-2 gap-3 mb-6">
      {[
        { icon: BarChart3, label: 'Shooting Splits' },
        { icon: TrendingUp, label: 'Advanced Stats' },
        { icon: Trophy, label: 'Achievements' },
        { icon: Video, label: 'Highlights' },
      ].map(({ icon: Icon, label }) => (
        <div 
          key={label}
          className="bg-white/5 border border-white/10 rounded-lg p-3 flex items-center gap-2"
        >
          <Lock className="h-4 w-4 text-zinc-600" />
          <span className="text-xs text-zinc-500">{label}</span>
        </div>
      ))}
    </div>

    <Button 
      className="bg-amber-500 hover:bg-amber-400 text-black font-bold uppercase tracking-wide"
      asChild
    >
      <Link to="/membership">
        <Crown className="h-4 w-4 mr-2" />
        Upgrade to Elite
      </Link>
    </Button>
  </div>
);

/**
 * Stats Not Available - Athlete not linked to SportsVisio
 */
const StatsNotAvailable = ({ athleteName }) => (
  <div className="text-center py-8">
    <div className="w-16 h-16 rounded-full bg-zinc-800 flex items-center justify-center mx-auto mb-4">
      <BarChart3 className="h-8 w-8 text-zinc-600" />
    </div>
    <h3 
      className="text-lg font-bold text-white mb-2"
      style={{ fontFamily: 'Oswald, sans-serif' }}
    >
      Stats Not Available Yet
    </h3>
    <p className="text-zinc-500 text-sm max-w-xs mx-auto">
      Advanced stats for {athleteName} will appear here once their games are recorded and processed by our AI system.
    </p>
  </div>
);

/**
 * Stats Pending - Linked but not synced
 */
const StatsPending = ({ athleteName }) => (
  <div className="text-center py-8">
    <div className="w-16 h-16 rounded-full bg-[#ff0000]/20 flex items-center justify-center mx-auto mb-4">
      <Loader2 className="h-8 w-8 text-[#ff0000] animate-spin" />
    </div>
    <h3 
      className="text-lg font-bold text-white mb-2"
      style={{ fontFamily: 'Oswald, sans-serif' }}
    >
      Processing Stats
    </h3>
    <p className="text-zinc-500 text-sm max-w-xs mx-auto">
      {athleteName}'s game footage is being analyzed. Advanced stats will appear here shortly.
    </p>
  </div>
);

/**
 * Full Stats View - For Elite users with synced data
 */
const FullStatsView = ({ athlete, stats }) => {
  // Use cached stats or fall back to basic athlete stats
  const displayStats = stats?.aggregated || athlete.stats || {};
  const trophies = stats?.trophies || {};
  const aiSummary = stats?.ai_summary;
  const highlight = stats?.highlight;

  // Calculate shooting percentages
  const shootingSplits = {
    FG: displayStats.fgPct || calculatePct(displayStats.fieldGoals),
    '3PT': displayStats.threePtPct || calculatePct(displayStats.threePoints),
    FT: displayStats.ftPct || calculatePct(displayStats.freeThrows),
  };

  // Per-game averages
  const games = displayStats.totalGames || athlete.games || 1;
  const averages = {
    PPG: displayStats.PPG || round(displayStats.totalPoints / games),
    RPG: displayStats.RPG || round(displayStats.totalRebounds / games),
    APG: displayStats.APG || round(displayStats.assists / games),
    SPG: displayStats.SPG || round(displayStats.steals / games),
    BPG: displayStats.BPG || round(displayStats.blocks / games),
  };

  return (
    <div className="space-y-5">
      {/* Trophies/Badges */}
      {Object.keys(trophies).length > 0 && (
        <div>
          <h4 
            className="text-[10px] text-zinc-500 uppercase tracking-[0.2em] font-bold mb-3"
            style={{ fontFamily: 'Manrope, sans-serif' }}
          >
            Achievements
          </h4>
          <div className="flex flex-wrap gap-2">
            {Object.entries(trophies).map(([key, count]) => {
              if (!count || count === 0) return null;
              const config = TROPHY_CONFIG[key];
              if (!config) return null;
              const Icon = config.icon;
              return (
                <Badge 
                  key={key}
                  className={`${config.color} border text-xs px-2.5 py-1 flex items-center gap-1.5`}
                >
                  <Icon className="h-3.5 w-3.5" />
                  {config.label}
                  {count > 1 && <span className="opacity-70">×{count}</span>}
                </Badge>
              );
            })}
          </div>
        </div>
      )}

      {/* Shooting Splits */}
      <div>
        <h4 
          className="text-[10px] text-zinc-500 uppercase tracking-[0.2em] font-bold mb-3"
          style={{ fontFamily: 'Manrope, sans-serif' }}
        >
          Shooting Splits
        </h4>
        <div className="grid grid-cols-3 gap-3">
          {Object.entries(shootingSplits).map(([label, value]) => (
            <div 
              key={label}
              className="bg-[#141414] border border-white/10 rounded-xl p-4 text-center"
            >
              <div 
                className="text-2xl font-black text-white"
                style={{ fontFamily: 'Oswald, sans-serif' }}
              >
                {value || '-'}%
              </div>
              <div 
                className="text-[10px] text-zinc-500 uppercase tracking-wider font-bold mt-1"
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                {label}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Per-Game Averages */}
      <div>
        <h4 
          className="text-[10px] text-zinc-500 uppercase tracking-[0.2em] font-bold mb-3"
          style={{ fontFamily: 'Manrope, sans-serif' }}
        >
          Per-Game Averages
        </h4>
        <div className="grid grid-cols-5 gap-2">
          {Object.entries(averages).map(([label, value]) => (
            <div 
              key={label}
              className="bg-[#141414] border border-white/10 rounded-lg p-3 text-center"
            >
              <div 
                className="text-lg font-black text-[#00d4ff]"
                style={{ fontFamily: 'Oswald, sans-serif' }}
              >
                {value || '-'}
              </div>
              <div 
                className="text-[9px] text-zinc-600 uppercase tracking-wider font-bold"
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                {label}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* AI Summary */}
      {aiSummary && (
        <div>
          <h4 
            className="text-[10px] text-zinc-500 uppercase tracking-[0.2em] font-bold mb-3 flex items-center gap-2"
            style={{ fontFamily: 'Manrope, sans-serif' }}
          >
            <Sparkles className="h-3.5 w-3.5 text-[#ff0000]" />
            AI Performance Summary
          </h4>
          <div className="bg-gradient-to-r from-[#ff0000]/10 to-transparent border border-[#ff0000]/20 rounded-xl p-4">
            <p className="text-sm text-zinc-300 leading-relaxed">
              {aiSummary}
            </p>
          </div>
        </div>
      )}

      {/* Highlight Clip */}
      {highlight?.thumbnailUrl && (
        <div>
          <h4 
            className="text-[10px] text-zinc-500 uppercase tracking-[0.2em] font-bold mb-3 flex items-center gap-2"
            style={{ fontFamily: 'Manrope, sans-serif' }}
          >
            <Video className="h-3.5 w-3.5 text-[#ff0000]" />
            Latest Highlight
          </h4>
          <a 
            href={highlight.url}
            target="_blank"
            rel="noopener noreferrer"
            className="block relative rounded-xl overflow-hidden group"
          >
            <img 
              src={highlight.thumbnailUrl} 
              alt="Highlight"
              className="w-full aspect-video object-cover"
            />
            <div className="absolute inset-0 bg-black/40 flex items-center justify-center group-hover:bg-black/30 transition-colors">
              <div className="w-14 h-14 rounded-full bg-[#ff0000] flex items-center justify-center">
                <Play className="h-6 w-6 text-white ml-1" />
              </div>
            </div>
            {highlight.duration && (
              <Badge className="absolute bottom-3 right-3 bg-black/70 text-white text-xs">
                {formatDuration(highlight.duration)}
              </Badge>
            )}
          </a>
        </div>
      )}

      {/* View Full Profile Link */}
      <Button 
        variant="outline" 
        className="w-full border-white/10 text-zinc-400 hover:text-white hover:bg-white/5"
        asChild
      >
        <Link to={`/athlete/${athlete.id}`}>
          View Full Profile
          <ChevronRight className="h-4 w-4 ml-2" />
        </Link>
      </Button>
    </div>
  );
};

// Utility functions
const calculatePct = (shotData) => {
  if (!shotData || !shotData.attempts || shotData.attempts === 0) return null;
  return Math.round((shotData.made / shotData.attempts) * 100);
};

const round = (num) => {
  if (!num || isNaN(num)) return null;
  return Math.round(num * 10) / 10;
};

const formatDuration = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

export default AdvancedStatsModal;
